import { cn } from '@/utils/cn';
import { Skeleton } from './Spinner';

/**
 * A skeleton row for card/list layouts. Composes `Skeleton` blocks into the
 * shape of a typical row so lists feel faster than a centred spinner.
 */
export function SkeletonRow({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3.5',
        className,
      )}
    >
      <Skeleton className="h-9 w-9 rounded-md" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3.5 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-7 w-16 rounded-md" />
    </div>
  );
}

/** A block of skeleton rows for list pages. */
export function SkeletonList({
  rows = 5,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  );
}

/** A grid of skeleton summary/stat cards. */
export function SkeletonCards({
  count = 4,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={cn('grid grid-cols-2 md:grid-cols-4 gap-3', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-neutral-200 bg-white px-4 py-3 space-y-2"
        >
          <Skeleton className="h-2.5 w-1/2" />
          <Skeleton className="h-5 w-3/4" />
        </div>
      ))}
    </div>
  );
}
